/**
 * ID prefixes and generators (docs/53 §2.1): a prefix, an underscore and 26 characters of RFC 4648 base32
 * (130 bits of randomness from crypto.getRandomValues). Never sequential, never guessable.
 *
 * Validation accepts 6 to 40 lowercase letters and digits after the prefix, so the ids that already exist in
 * fixtures (`tnt_pinecroft01`, `site_...`) still parse. Only `newId` mints new ones.
 */
import { z } from 'zod';

export const ID_PREFIXES = {
  tenant: 'tnt',
  site: 'site',
  fact: 'fct',
  service: 'svc',
  location: 'loc',
  pod: 'pod',
  user: 'usr',
  invite: 'inv',
  loginChallenge: 'lch',
  device: 'dev',
  emailChange: 'emc',
  staff: 'stf',
  viewSession: 'vws',
  termsAcceptance: 'tac',
  pushSubscription: 'psb',
  notification: 'ntf',
  delivery: 'dlv',
  clockInterval: 'clk',
  finding: 'fnd',
  domainConnect: 'dcn',
  lead: 'ld',
  review: 'rev',
  reviewRequest: 'rr',
  thread: 'thr',
  message: 'msg',
  mediaAsset: 'med',
  upload: 'upl',
  job: 'job',
  output: 'out',
  project: 'prj',
  notice: 'ntc',
  request: 'req',
  requestMessage: 'rqm',
  proposal: 'prp',
  decision: 'dec',
  approval: 'apv',
  credit: 'crd',
  gridRun: 'grd',
  scanGrid: 'sgr',
  report: 'rpt',
  oauthToken: 'oat',
  integration: 'int',
  export: 'exp',
  deletionJob: 'del',
  auditEvent: 'aud',
  event: 'evt',
  queueMessage: 'qm',
  requestId: 'rqi',
  gbpTask: 'gbt',
  workflow: 'wf',
  clientNote: 'cn',
  opsAlert: 'alr',
  supportTicket: 'sup',
} as const;

export type IdKind = keyof typeof ID_PREFIXES;
export type IdPrefix = (typeof ID_PREFIXES)[IdKind];

const BASE32 = 'abcdefghijklmnopqrstuvwxyz234567';
const EXAMPLE_TAIL = 'abcdefghijklmnopqrstuvwxyz';

/** Mint a new id: prefix + 26 base32 characters (130 random bits). Works in Workers, browsers and Node 20+. */
export function newId<K extends IdKind>(kind: K): `${(typeof ID_PREFIXES)[K]}_${string}` {
  const bytes = new Uint8Array(26);
  crypto.getRandomValues(bytes);
  let tail = '';
  for (const b of bytes) tail += BASE32[b & 31];
  return `${ID_PREFIXES[kind]}_${tail}` as `${(typeof ID_PREFIXES)[K]}_${string}`;
}

function idSchema<K extends IdKind>(kind: K) {
  const prefix = ID_PREFIXES[kind];
  return z
    .string()
    .regex(new RegExp(`^${prefix}_[a-z0-9]{6,40}$`), `expected a ${kind} id (${prefix}_...)`)
    .meta({ examples: [`${prefix}_${EXAMPLE_TAIL}`], idKind: kind });
}

export const TenantId = idSchema('tenant');
export const OpsAlertId = idSchema('opsAlert');
export const SiteId = idSchema('site');
export const FactId = idSchema('fact');
export const ServiceId = idSchema('service');
export const LocationId = idSchema('location');
export const PodId = idSchema('pod');
export const UserId = idSchema('user');
export const InviteId = idSchema('invite');
export const DeviceId = idSchema('device');
export const StaffId = idSchema('staff');
export const ViewSessionId = idSchema('viewSession');
export const PushSubscriptionId = idSchema('pushSubscription');
export const NotificationId = idSchema('notification');
export const DeliveryId = idSchema('delivery');
export const DomainConnectId = idSchema('domainConnect');
export const LeadId = idSchema('lead');
export const ReviewId = idSchema('review');
export const ReviewRequestId = idSchema('reviewRequest');
export const ThreadId = idSchema('thread');
export const MessageId = idSchema('message');
export const MediaAssetId = idSchema('mediaAsset');
export const UploadId = idSchema('upload');
export const JobId = idSchema('job');
export const OutputId = idSchema('output');
export const ProjectId = idSchema('project');
export const NoticeId = idSchema('notice');
export const RequestId = idSchema('request');
export const RequestMessageId = idSchema('requestMessage');
export const ProposalId = idSchema('proposal');
export const DecisionId = idSchema('decision');
export const ApprovalId = idSchema('approval');
export const CreditId = idSchema('credit');
export const GridRunId = idSchema('gridRun');
export const ScanGridId = idSchema('scanGrid');
export const ReportId = idSchema('report');
export const IntegrationId = idSchema('integration');
export const ExportId = idSchema('export');
export const AuditEventId = idSchema('auditEvent');
export const EventId = idSchema('event');
export const QueueMessageId = idSchema('queueMessage');
export const TraceId = idSchema('requestId');
export const GbpTaskId = idSchema('gbpTask');
export const SupportTicketId = idSchema('supportTicket');
export const WorkflowId = idSchema('workflow');
export const FindingId = idSchema('finding');

/** A town id is the integer primary key of the global `towns` table. */
export const TownId = z.number().int().positive().meta({ examples: [4821] });

/** Public reference to a session: the first 16 hex characters of the stored SHA-256. The cookie never leaves the browser. */
export const SessionRef = z.string().regex(/^[a-f0-9]{16}$/).meta({ examples: ['3f9a1c0b7d2e4a68'] });

/** True when the string is an id of the given kind. */
export function isId(kind: IdKind, value: unknown): boolean {
  return typeof value === 'string' && new RegExp(`^${ID_PREFIXES[kind]}_[a-z0-9]{6,40}$`).test(value);
}
